import { useEffect, useState } from "react";
import api, { INR, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Download, Share2, FileText, Eye } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { amountInWords } from "@/lib/amountWords";

// Dealer info kept in sync with the Invoice PDF (see backend get_invoice)
const DEALER = {
  name: "M/s India Atul Motors",
  address: "Near Ufrail Chowk, NH-31 Bypass Road, Maranga, Purnea, Bihar - 854303",
  gstin: "10FXZPS6916J1ZN",
  phone: "9031073394",
  email: "indiaauto.atul@gmail.com",
};

async function loadLogoDataURL() {
  try {
    const res = await fetch("/atul-logo.png");
    const blob = await res.blob();
    return await new Promise((r) => {
      const rd = new FileReader();
      rd.onloadend = () => r(rd.result);
      rd.readAsDataURL(blob);
    });
  } catch (e) { return null; }
}

async function buildQuotationPDF(q, mode = "save") {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();

  // Header — mirrors Invoice header
  doc.setFillColor(30, 58, 138);
  doc.rect(0, 0, W, 100, "F");
  const logoData = await loadLogoDataURL();
  if (logoData) {
    doc.setFillColor(0, 0, 0);
    doc.roundedRect(28, 18, 64, 64, 6, 6, "F");
    try { doc.addImage(logoData, "JPEG", 32, 22, 56, 56, undefined, "FAST"); } catch (e) { /* ignore */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.text(DEALER.name, 110, 40);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(DEALER.address, 110, 58);
  doc.text(`GSTIN: ${DEALER.gstin}`, 110, 72);
  doc.text(`Ph: ${DEALER.phone}   Email: ${DEALER.email}`, 110, 86);

  doc.setFillColor(249, 115, 22);
  doc.rect(W - 200, 20, 160, 60, "F");
  doc.setFontSize(9);
  doc.text("QUOTATION", W - 190, 38);
  doc.setFont("helvetica", "bold"); doc.setFontSize(12);
  doc.text(q.quotation_number || "PREVIEW", W - 190, 56);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  doc.text(new Date(q.created_at || Date.now()).toLocaleDateString("en-IN"), W - 190, 72);

  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("Customer", 40, 140);
  doc.text("Vehicle", W / 2 + 20, 140);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text(q.customer_name || "—", 40, 156);
  doc.text(q.customer_phone || "", 40, 170);
  const addr = doc.splitTextToSize(q.customer_address || "", 220);
  doc.text(addr, 40, 184);

  doc.text(`Model: ${q.vehicle_model}`, W / 2 + 20, 156);
  if (q.vehicle_color) doc.text(`Color: ${q.vehicle_color}`, W / 2 + 20, 170);
  if (q.branch) doc.text(`Branch: ${q.branch}`, W / 2 + 20, 184);

  // Price
  autoTable(doc, {
    startY: 230,
    head: [["Description", "Amount (INR)"]],
    body: [
      ["Ex-Showroom Price", INR(q.ex_showroom_price)],
      ["Insurance", INR(q.insurance_price)],
      ["RTO Charges", INR(q.rto_charges)],
      ["Permit Charges", INR(q.permit_charges)],
      ["Earthing Charges", INR(q.earthing_charges)],
      ...(Number(q.finance_charges || 0) > 0 ? [["Finance Charge", INR(q.finance_charges)]] : []),
      [{ content: "Subtotal", styles: { fontStyle: "bold" } }, { content: INR(q.subtotal), styles: { fontStyle: "bold" } }],
      ...(Number(q.discount || 0) > 0 ? [["Discount", "- " + INR(q.discount)]] : []),
      [{ content: "On-Road Price", styles: { fontStyle: "bold", fillColor: [255, 237, 213] } },
       { content: INR(q.on_road_price), styles: { fontStyle: "bold", fillColor: [255, 237, 213] } }],
    ],
    headStyles: { fillColor: [30, 58, 138], textColor: 255 },
    styles: { fontSize: 10 },
    columnStyles: { 1: { halign: "right" } },
  });

  // Financing — Interest Rate is intentionally hidden on printable PDF
  if (Number(q.loan_amount || 0) > 0 && Number(q.tenure_months || 0) > 0) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 15,
      head: [["Finance Details", "Value"]],
      body: [
        ["Down Payment", INR(q.down_payment)],
        ["Loan Amount", INR(q.loan_amount)],
        ["Tenure", `${q.tenure_months} months`],
        [{ content: "Monthly EMI", styles: { fontStyle: "bold" } }, { content: INR(q.emi), styles: { fontStyle: "bold" } }],
        ["Total Payable (Loan)", INR(q.total_payable)],
      ],
      headStyles: { fillColor: [249, 115, 22], textColor: 255 },
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: "right" } },
    });
  }

  const yEnd = doc.lastAutoTable.finalY + 20;
  const centerX = W / 2;
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text(`On-Road Price: ${INR(q.on_road_price)}`, centerX, yEnd, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  const words = doc.splitTextToSize(`(${amountInWords(q.on_road_price)})`, W - 80);
  doc.text(words, centerX, yEnd + 14, { align: "center" });
  doc.setTextColor(15, 23, 42);

  const validTxt = q.valid_until
    ? `Valid until: ${new Date(q.valid_until).toLocaleDateString("en-IN")}`
    : `Valid for ${q.valid_days || 7} days`;
  doc.setFontSize(9);
  doc.text(validTxt, 40, yEnd + 60);
  if (q.notes) {
    const notes = doc.splitTextToSize(`Notes: ${q.notes}`, W - 80);
    doc.text(notes, 40, yEnd + 76);
  }

  // Footer — matches Invoice tone
  doc.setFontSize(8); doc.setTextColor(100, 116, 139);
  doc.text("* Quotation only. Prices are indicative and subject to change until booking.", 40, yEnd + 110);
  doc.text(`For ${DEALER.name}`, W - 40, yEnd + 110, { align: "right" });

  if (mode === "blob") return doc.output("blob");
  doc.save(`${q.quotation_number || "quotation"}.pdf`);
}

export default function Quotations() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewing, setViewing] = useState(null);

  const fetchAll = async () => {
    const { data } = await api.get("/quotations");
    setList(data);
  };
  useEffect(() => { fetchAll(); }, []);

  const remove = async (q) => {
    if (!window.confirm(`Are you sure you want to delete quotation ${q.quotation_number}?`)) return;
    try { await api.delete(`/quotations/${q.id}`); toast.success("Quotation deleted"); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const download = async (q) => {
    try { await buildQuotationPDF(q, "save"); }
    catch (e) { toast.error("PDF generation failed"); }
  };

  const shareWA = async (q) => {
    const lines = [
      `*${DEALER.name}* — Vehicle Quotation`,
      `Quotation: ${q.quotation_number}`,
      `Customer: ${q.customer_name}`,
      `Vehicle: ${q.vehicle_model}`,
      `On-Road Price: ₹${(q.on_road_price || 0).toLocaleString("en-IN")}`,
      ...(Number(q.emi || 0) > 0 ? [
        `EMI: ₹${(q.emi || 0).toLocaleString("en-IN")}/mo × ${q.tenure_months} months`,
        `Down Payment: ₹${(q.down_payment || 0).toLocaleString("en-IN")}`,
      ] : []),
      `Valid until: ${new Date(q.valid_until).toLocaleDateString("en-IN")}`,
      `Contact: ${DEALER.phone}`,
    ].join("\n");
    try {
      const blob = await buildQuotationPDF(q, "blob");
      if (blob && navigator.canShare) {
        const file = new File([blob], `${q.quotation_number}.pdf`, { type: "application/pdf" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ title: `${DEALER.name} — ${q.quotation_number}`, text: lines, files: [file] });
          return;
        }
      }
    } catch (e) { /* fallback */ }
    const digits = (q.customer_phone || "").replace(/\D/g, "");
    const url = digits
      ? `https://wa.me/91${digits.slice(-10)}?text=${encodeURIComponent(lines)}`
      : `https://wa.me/?text=${encodeURIComponent(lines)}`;
    await buildQuotationPDF(q, "save");
    window.open(url, "_blank");
    toast.info("WhatsApp opened — PDF downloaded, attach it if needed");
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="quotations-heading">Customer Quotations</h1>
          <p className="text-sm text-slate-500 mt-1">Generate quotations with EMI calculator, PDF download and WhatsApp share.</p>
        </div>
        <button data-testid="new-quotation-button" onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-orange bg-brand-orange-hover text-white text-sm">
          <Plus className="w-4 h-4" /> New Quotation
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg table-scroll">
        <table className="data-table w-full" data-testid="quotations-table">
          <thead>
            <tr>
              <th className="col-sr">SR. No.</th>
              <th>Quotation #</th><th>Customer</th><th>Vehicle</th>
              <th className="text-right">On-Road</th><th className="text-right">EMI</th>
              <th>Valid Until</th><th className="text-right pr-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan="8" className="text-center text-slate-400 py-8">No quotations yet.</td></tr>
            )}
            {list.map((q, idx) => (
              <tr key={q.id} data-testid={`quotation-row-${q.id}`}>
                <td className="col-sr">{idx + 1}</td>
                <td className="font-medium text-slate-900">{q.quotation_number}</td>
                <td>
                  <div className="text-sm">{q.customer_name}</div>
                  <div className="text-xs text-slate-500">{q.customer_phone}</div>
                </td>
                <td>{q.vehicle_model}</td>
                <td className="text-right">{INR(q.on_road_price)}</td>
                <td className="text-right">{Number(q.emi || 0) > 0 ? `${INR(q.emi)}/mo` : "—"}</td>
                <td className="text-xs text-slate-500">
                  {q.valid_until ? new Date(q.valid_until).toLocaleDateString("en-IN") : "—"}
                </td>
                <td className="text-right pr-6">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setViewing(q)} data-testid={`view-quotation-${q.id}`}
                      className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="View">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => download(q)} data-testid={`download-quotation-${q.id}`}
                      className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600" title="Download PDF">
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => shareWA(q)} data-testid={`share-quotation-${q.id}`}
                      className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-600" title="WhatsApp">
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(q)} data-testid={`delete-quotation-${q.id}`}
                      className="p-1.5 rounded-md hover:bg-red-50 text-red-500" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <QuotationForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchAll(); }} />
      )}
      {viewing && <QuotationView q={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function QuotationForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_address: "",
    vehicle_model: "", vehicle_color: "",
    ex_showroom_price: 0, insurance_price: 0, rto_charges: 0, permit_charges: 0,
    earthing_charges: 0, finance_charges: 0, discount: 0,
    down_payment: 0, loan_amount: 0, interest_rate: 10.5, tenure_months: 36,
    valid_days: 7, notes: "",
  });
  const [preview, setPreview] = useState({ subtotal: 0, on_road_price: 0, emi: 0, total_interest: 0, total_payable: 0, down_payment: 0, loan_amount: 0 });
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [modelHints, setModelHints] = useState([]);

  useEffect(() => {
    api.get("/branches").then(({ data }) => {
      setBranches(data.branches);
      setBranch(data.branches[0] || "");
    });
    api.get("/models").then(({ data }) => setModelHints(data.models || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const subtotal = Number(form.ex_showroom_price || 0) + Number(form.insurance_price || 0)
      + Number(form.rto_charges || 0) + Number(form.permit_charges || 0)
      + Number(form.earthing_charges || 0) + Number(form.finance_charges || 0);
    const onRoad = Math.max(subtotal - Number(form.discount || 0), 0);
    let loan = Number(form.loan_amount || 0);
    let down = Number(form.down_payment || 0);
    if (loan === 0 && down > 0) loan = Math.max(onRoad - down, 0);
    if (down === 0 && loan > 0) down = Math.max(onRoad - loan, 0);
    const r = Number(form.interest_rate || 0) / 100 / 12;
    const n = Number(form.tenure_months || 0);
    let emi = 0;
    if (loan > 0 && n > 0) emi = r === 0 ? loan / n : loan * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    const totalPay = emi * n;
    setPreview({
      subtotal: Math.round(subtotal), on_road_price: Math.round(onRoad),
      emi: Math.round(emi * 100) / 100, total_interest: Math.round((totalPay - loan) * 100) / 100,
      total_payable: Math.round(totalPay * 100) / 100, down_payment: Math.round(down), loan_amount: Math.round(loan),
    });
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_model.trim()) { toast.error("Vehicle Model is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        vehicle_model: form.vehicle_model.trim(),
        ex_showroom_price: Number(form.ex_showroom_price) || 0,
        insurance_price: Number(form.insurance_price) || 0,
        rto_charges: Number(form.rto_charges) || 0,
        permit_charges: Number(form.permit_charges) || 0,
        earthing_charges: Number(form.earthing_charges) || 0,
        finance_charges: Number(form.finance_charges) || 0,
        discount: Number(form.discount) || 0,
        down_payment: Number(form.down_payment) || 0,
        loan_amount: Number(form.loan_amount) || 0,
        interest_rate: Number(form.interest_rate) || 0,
        tenure_months: Number(form.tenure_months) || 0,
        valid_days: Number(form.valid_days) || 7,
        branch,
      };
      await api.post("/quotations", payload);
      toast.success("Quotation created");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const num = (k) => (
    <input type="number" min="0" step="1" value={form[k]}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      className="input" data-testid={`qf-${k}`} />
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="quotation-form-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 my-8">
        <div className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-brand-orange" /> New Quotation
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</div>
            <Field label="Name">
              <input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="input" data-testid="qf-customer_name" />
            </Field>
            <Field label="Phone">
              <input required value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="input" data-testid="qf-customer_phone" />
            </Field>
            <Field label="Address">
              <input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="input" data-testid="qf-customer_address" />
            </Field>

            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 pt-3">Vehicle</div>
            <Field label="Model">
              <input list="qf-model-hints" required value={form.vehicle_model}
                onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                className="input" data-testid="qf-vehicle_model" placeholder="Model name" />
              <datalist id="qf-model-hints">
                {modelHints.map(m => <option key={m} value={m} />)}
              </datalist>
            </Field>
            <Field label="Color">
              <input value={form.vehicle_color} onChange={(e) => setForm({ ...form, vehicle_color: e.target.value })} className="input" data-testid="qf-vehicle_color" />
            </Field>
            <Field label="Branch">
              <select value={branch} onChange={(e) => setBranch(e.target.value)} className="input" data-testid="qf-branch">
                {branches.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
          </div>

          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Price Break-up (INR)</div>
            <Field label="Ex-Showroom Price">{num("ex_showroom_price")}</Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Insurance">{num("insurance_price")}</Field>
              <Field label="RTO Charges">{num("rto_charges")}</Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Permit Charges">{num("permit_charges")}</Field>
              <Field label="Earthing Charges">{num("earthing_charges")}</Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Finance Charge">{num("finance_charges")}</Field>
              <Field label="Discount">{num("discount")}</Field>
            </div>

            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 pt-3">EMI Calculator</div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Down Payment">{num("down_payment")}</Field>
              <Field label="Loan Amount">{num("loan_amount")}</Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Interest Rate (% p.a.) — internal only">
                <input type="number" min="0" step="0.1" value={form.interest_rate}
                  onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                  className="input" data-testid="qf-interest_rate" />
              </Field>
              <Field label="Tenure (months)">{num("tenure_months")}</Field>
            </div>
            <Field label="Validity (days)">
              <input type="number" min="1" value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: e.target.value })} className="input" data-testid="qf-valid_days" />
            </Field>
            <Field label="Notes">
              <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" data-testid="qf-notes" />
            </Field>
          </div>
        </div>

        <div className="mt-6 p-4 bg-slate-50 rounded-md border border-slate-200 grid grid-cols-4 gap-3" data-testid="qf-preview">
          <Stat label="Subtotal" value={INR(preview.subtotal)} />
          <Stat label="On-Road Price" value={INR(preview.on_road_price)} highlight />
          <Stat label="Down Payment" value={INR(preview.down_payment)} />
          <Stat label="Loan Amount" value={INR(preview.loan_amount)} />
          <Stat label="Monthly EMI" value={preview.emi > 0 ? `${INR(preview.emi)}/mo` : "—"} highlight />
          <Stat label="Tenure" value={form.tenure_months ? `${form.tenure_months} mo` : "—"} />
          <Stat label="Total Interest" value={INR(preview.total_interest)} />
          <Stat label="Total Payable" value={INR(preview.total_payable)} />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-slate-300" data-testid="qf-cancel">Cancel</button>
          <button type="submit" disabled={saving}
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60" data-testid="qf-submit">
            {saving ? "Saving…" : "Save Quotation"}
          </button>
        </div>
        <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #CBD5E1; border-radius: 0.375rem; font-size: 0.875rem; outline: none; background: white; }`}</style>
      </form>
    </div>
  );
}

function QuotationView({ q, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="quotation-view-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
        <div className="font-heading text-xl font-semibold mb-3">Quotation {q.quotation_number}</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-slate-500">Customer:</span> {q.customer_name}</div>
          <div><span className="text-slate-500">Phone:</span> {q.customer_phone}</div>
          <div><span className="text-slate-500">Model:</span> {q.vehicle_model}</div>
          <div><span className="text-slate-500">Branch:</span> {q.branch}</div>
          <div><span className="text-slate-500">Ex-Showroom:</span> {INR(q.ex_showroom_price)}</div>
          <div><span className="text-slate-500">On-Road:</span> <b>{INR(q.on_road_price)}</b></div>
          <div><span className="text-slate-500">Down Payment:</span> {INR(q.down_payment)}</div>
          <div><span className="text-slate-500">Loan:</span> {INR(q.loan_amount)}</div>
          <div><span className="text-slate-500">EMI:</span> <b>{q.emi > 0 ? `${INR(q.emi)}/mo × ${q.tenure_months}` : "—"}</b></div>
          <div><span className="text-slate-500">Finance Charge:</span> {INR(q.finance_charges)}</div>
          <div><span className="text-slate-500">Total Payable:</span> {INR(q.total_payable)}</div>
          <div><span className="text-slate-500">Valid Until:</span> {new Date(q.valid_until).toLocaleDateString("en-IN")}</div>
        </div>
        {q.notes && <div className="mt-3 text-xs text-slate-600 bg-slate-50 p-3 rounded">Notes: {q.notes}</div>}
        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm bg-slate-800 text-white" data-testid="qv-close">Close</button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
    {children}
  </label>
);

const Stat = ({ label, value, highlight }) => (
  <div className={`p-3 rounded-md ${highlight ? "bg-orange-50 border border-orange-200" : "bg-white border border-slate-200"}`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-sm font-bold ${highlight ? "text-brand-orange" : "text-slate-900"}`}>{value}</div>
  </div>
);
